import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useForUserType } from "hooks/useForUserType";
import { ChipTaskStatus } from "pages/components/chips/chip-tasks-status";
import { ISelectableItem } from "pages/components/selects/select-with-search";
import { SelectableManagerWithSearch } from "pages/components/selects/selectable-manager-with-search";
import { TimerCountDown } from "pages/main/components/TimerCountDown";
import { OrderStatusBadge } from "pages/main/components/order-status-badge";
import { useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { IRootState } from "redux/types";
import { UserType } from "service";
import { EAssignerType } from "types";
import { AssignmentThreeDotsButton } from "./AssignmentThreeDotsButton";

const assignerColumns = [
  {
    name: "Цифровой",
    key: EAssignerType.digital,
  },
  {
    name: "Финансовый",
    key: EAssignerType.finance,
  },
  {
    name: "Юридический",
    key: EAssignerType.legal,
  },
];

export const AssignmentsRegistryTable = () => {
  return (
    <Table>
      <AssignmentsRegistryTableHead />
      <AssignmentsRegistryTableBody />
    </Table>
  );
};

export const AssignmentsRegistryTableHead = () => {
  return (
    <TableHead>
      <TableRow
        sx={{
          backgroundColor: "#F9FAFB",
          ".MuiTableCell-root": {
            padding: "12px",
            fontSize: "12px",
            lineHeight: "18px",
            fontWeight: "500",
            color: "#475467",
          },
        }}
      >
        <TableCell>#</TableCell>
        <TableCell>Заявитель</TableCell>
        <TableCell>Ответственный</TableCell>
        <TableCell>Вид Затрат</TableCell>
        <TableCell>Тип Задачи</TableCell>
        <TableCell>Прогресс</TableCell>
        <TableCell>Отсчет</TableCell>
        <TableCell>Заявленная сумма</TableCell>
        <TableCell>Статус</TableCell>
        <TableCell></TableCell>
      </TableRow>
    </TableHead>
  );
};

export const AssignmentsRegistryTableBody = () => {
  const { isIt } = useForUserType();
  const { assignments, managers } = useSelector(
    (state: IRootState) => state.assignments,
    shallowEqual
  );

  const optionsManagers = useMemo(() => {
    return (
      managers.value?.map((manager) => {
        return { label: manager.full_name, value: manager.user_id };
      }) ?? []
    );
  }, [managers, managers.loading]);

  if (assignments.loading || assignments.value?.list.length === 0)
    return (
      <TableBody>
        <TableRow
          sx={{
            height: "calc(80px * 10)",
          }}
        >
          <TableCell colSpan={8} sx={{ textAlign: "center" }}>
            {assignments.loading && <CircularProgress />}
            {!assignments.loading && assignments.value?.list.length === 0 && (
              <Typography>Нет данных</Typography>
            )}
          </TableCell>
        </TableRow>
      </TableBody>
    );

  return (
    <TableBody>
      {!!assignments.value &&
        assignments.value.list.map((assignment) => {
          return (
            <AssignmentsRegistryTableBodyRow
              key={assignment.assignment_id}
              assignment={assignment}
              managers={optionsManagers}
              canEdit={isIt(UserType.Admin)}
            />
          );
        })}
    </TableBody>
  );
};

export const AssignmentsRegistryTableBodyRow = ({
  assignment,
  managers,
  canEdit,
}: {
  assignment;
  managers: ISelectableItem[];
  canEdit: boolean;
}) => {
  return (
    <TableRow
      sx={{
        ".MuiTableCell-root": {
          padding: "16px 12px",
        },
      }}
    >
      <TableCell>
        <Typography color={"#475467"}>{assignment.id}</Typography>
      </TableCell>
      <TableCell>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography fontWeight={"500"}>
            {assignment.applicant_name}
          </Typography>
          <Typography color={"#475467"}>{assignment.applicant_bin}</Typography>
        </Box>
      </TableCell>
      <TableCell>
        {canEdit ? (
          <SelectableManagerWithSearch
            assignmentId={assignment.assignment_id}
            selectedManager={{
              label: assignment.assignee_name,
              value: assignment.assignee_id ?? "empty",
            }}
            optionsManagers={managers}
          />
        ) : (
          <Typography color={"#475467"}>{assignment.assignee_name}</Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography color={"#475467"}>{assignment.sheet_title}</Typography>
      </TableCell>
      <TableCell>
        <Typography color={"#475467"}>
          {
            {
              digital: "Оцифровка",
              finance: "Фин. часть",
              legal: "Юр. часть",
            }[assignment.assignment_type]
          }
        </Typography>
      </TableCell>

      <TableCell color={"#475467"}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {assignerColumns.map((column) => (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "4px",
                "& *": {
                  fontSize: "10px !important",
                },
              }}
              key={column.key}
            >
              <Typography>{column.name}</Typography>
              <OrderStatusBadge
                assignmentId={assignment.assignment_id}
                status_name={assignment[`${column.key}_status`] ?? ""}
                withSwitch={assignment.assignment_type == column.key}
              />
            </Box>
          ))}
        </Box>
      </TableCell>

      <TableCell
        color={"#475467"}
        sx={{
          fontSize: "12px",
        }}
      >
        {assignment?.reply_end_at ? (
          <TimerCountDown
            endAtTimeStamp={new Date(assignment?.reply_end_at).getTime() / 1000}
          />
        ) : (
          "Отсутствует"
        )}
      </TableCell>

      <TableCell>
        <Typography>
          {(assignment.total_sum ?? 0).toLocaleString()} тг.
        </Typography>
      </TableCell>
      <TableCell>
        <ChipTaskStatus
          label={
            assignment.is_completed
              ? `Завершено (${assignment.rows_completed})`
              : `В работе (${assignment.rows_completed}/${assignment.total_rows})`
          }
          color={assignment.is_completed ? "green" : "orange"}
        />
      </TableCell>
      <TableCell>
        <AssignmentThreeDotsButton
          assignmentId={assignment.assignment_id}
          spreadsheetLink={assignment.link}
          applicationId={assignment.application_id}
          signLink={assignment.sign_link}
        />
      </TableCell>
    </TableRow>
  );
};
